import { track } from '@eventbrite/datalayer-library';
import React, { useEffect, useReducer } from 'react';
import { LISTING_HEAP_IDS } from '../../../constants';
import { handleFollowClick, hydrateFollowStatus } from './api';
import type { FollowButtonProps } from './FollowButton';
import './FollowButton.scss';
import {
    followOrganizerInitialState,
    followOrganizerReducer,
    SET_SHOULD_SHOW_GDPR_MODAL,
    SET_SHOULD_SHOW_LOGIN_MODAL,
} from './reducer';

type FollowButtonProviderProps = {
    isGDPRCountry: boolean;
    organizerId: string;
    userId?: number;
    isAuthenticated: boolean;
};

export const FollowButtonContext =
    React.createContext<FollowButtonProps | null>(null);

export const FollowButtonProvider: React.FC<FollowButtonProviderProps> = ({
    children,
    organizerId,
    userId,
    isGDPRCountry,
    isAuthenticated,
}) => {
    const [state, dispatch] = useReducer(
        followOrganizerReducer,
        followOrganizerInitialState,
    );
    const {
        followersCount,
        isFollowing,
        shouldShowLoginModal,
        shouldShowGDPRModal,
    } = state;

    useEffect(() => {
        hydrateFollowStatus({
            organizerId,
            userId,
            isGDPRCountry,
            isFollowing,
            isAuthenticated,
            dispatch,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onFollowClick = (pageArea: string) => {
        const followEventName = isFollowing
            ? LISTING_HEAP_IDS.UNFOLLOW_ORGANIZER
            : LISTING_HEAP_IDS.FOLLOW_ORGANIZER;

        track({
            eventName: followEventName,
            eventData: {
                organizerId: organizerId,
                pageArea: pageArea,
            },
        });
        return handleFollowClick({
            isFollowing,
            organizerId,
            userId,
            isAuthenticated,
            followersCount,
            isGDPRCountry,
            pageArea,
            dispatch,
        });
    };

    return (
        <FollowButtonContext.Provider
            value={{
                followersCount,
                isFollowing,
                shouldShowLoginModal,
                shouldShowGDPRModal,
                userId,
                onFollowClick,
                closeLoginModal: () => {
                    dispatch({
                        type: SET_SHOULD_SHOW_LOGIN_MODAL,
                        payload: false,
                    });
                },
                closeGDPRModal: () => {
                    dispatch({
                        type: SET_SHOULD_SHOW_GDPR_MODAL,
                        payload: false,
                    });
                },
            }}
        >
            {children}
        </FollowButtonContext.Provider>
    );
};
